import React, {
	useCallback,
	useMemo,
} from 'react';
import { DeleteOutlined } from '@ant-design/icons';
import uuid from 'short-uuid';
import {
	Checkbox,
	ConfigProvider,
	Tooltip,
} from 'antd';
import {
	EstimatedItemTypes,
	EstimateStatus,
} from '@shared/utils/constants';
import type {
	EstimatedExpenseItem,
	EstimatedRevenueItem,
} from '@api/models/cargo';
import type { EditableCargo } from '@api/features/cargos/updateCargo';
import Card from '@client/components/Card/Card';
import Button from '@client/components/Button';
import styles from '@client/screens/cargos/components/CommissionsCard/BrokerCommissionDetails.module.css';
import AddButton from '@client/components/AddButton';
import EditableCellTableRedux from '@client/components/EditableTableRedux/EditableCellTableRedux';
import { OnUpdateCargo } from '@client/screens/estimates/details/context/hooks/useCargoHandlers';
import { useAuth } from '@client/lib/auth';

type Props = {
	handleChange: OnUpdateCargo;
	cargoDetails: EditableCargo | undefined;
};

const EstimatedItemsCard = ({
	cargoDetails,
	handleChange,
}: Props) => {
	const disabled = cargoDetails?.status === EstimateStatus.Fixed && cargoDetails.voyageId == null;
	const { componentDisabled } = ConfigProvider.useConfig();
	const updateEstimatedItems = useCallback((
		updatedItems: Array<EstimatedRevenueItem | EstimatedExpenseItem>,
	) => {
		if (cargoDetails?.id == null) {
			return;
		}

		handleChange(cargoDetails.id, { estimatedItems: updatedItems });
	}, [cargoDetails?.id, handleChange]);

	const dataSource = useMemo(() => cargoDetails?.estimatedItems ?? [], [cargoDetails]);
	const { userInfo: { baseCurrency } } = useAuth();

	return (
		<Card
			slim
			title="Estimated Expenses / Revenues"
		>
			<EditableCellTableRedux<EstimatedExpenseItem | EstimatedRevenueItem>
				columns={[
					{
						title: 'Type',
						dataIndex: 'type',
						editable: !disabled,
						type: 'select',
						inputProps: {
							options: [{
								value: EstimatedItemTypes.EXPENSE,
								label: 'Expense',
							}, {
								value: EstimatedItemTypes.REVENUE,
								label: 'Revenue',
							}],
							showSearch: true,
						},
					},
					{
						title: 'Description',
						dataIndex: 'description',
						editable: !disabled,
						type: 'text',
					},
					{
						title: 'Amount',
						dataIndex: 'amount',
						editable: !disabled,
						type: 'currency',
						inputProps: {
							currency: cargoDetails?.currency ?? 'USD',
						},
					},
					{
						title: 'Subject to comm.',
						dataIndex: 'subjectToCommissions',
						editable: !disabled,
						type: 'checkbox',
						render: (record: EstimatedExpenseItem | EstimatedRevenueItem) => {
							let box = (
								<Tooltip title="Commissions are only applicable to revenue items">
									<Checkbox
										checked={false}
										disabled={record.type === EstimatedItemTypes.EXPENSE || disabled || componentDisabled}
									/>
								</Tooltip>
							);

							if (record.type === EstimatedItemTypes.REVENUE) {
								box = (
									<Checkbox
										disabled={disabled || componentDisabled}
										checked={record.subjectToCommissions}
										onChange={async (e) => {
											const items = cargoDetails?.estimatedItems ?? [];

											const updatedItems = items
												.map((item) => (item.id === record.id ?
													{
														...item,
														subjectToCommissions: e.target.checked,
													} as EstimatedRevenueItem :
													item
												));

											updateEstimatedItems(updatedItems);
										}}
									/>
								);
							}

							return box;
						},
					},
					{
						title: (
							<div className={styles.addCol}>
								<AddButton
									disabled={disabled}
									onClick={async () => {
										const existingItems = cargoDetails?.estimatedItems;
										const newRow: EstimatedExpenseItem = {
											id: uuid.generate(),
											type: EstimatedItemTypes.EXPENSE,
											amount: null,
											description: '',
											subjectToCommissions: false,
											referenceToModelId: null,
											exchangeRate: cargoDetails?.exchangeRate ?? 1,
											currency: cargoDetails?.currency ?? baseCurrency,
										};

										const updatedItems = [
											...(existingItems != null ?
												existingItems : []
											),
											newRow,
										];

										updateEstimatedItems(updatedItems);
									}}
								/>
							</div>
						),
						// @ts-ignore
						dataIndex: 'addEntry',
						render: (record: EstimatedExpenseItem | EstimatedRevenueItem) => (
							<Button
								onClick={async () => {
									const entries = cargoDetails?.estimatedItems ?? [];
									const filtered = entries.filter((e) => e.id !== record.id);
									updateEstimatedItems(filtered);
								}}
								type="text"
								confirmTitle="Are you sure you want to delete this row?"
								danger
								icon={(<DeleteOutlined />)}
								className={styles.addCol}
								disabled={disabled}
							/>
						),
						className: styles.addCol,
						width: 50,
					},
				]}
				allowAddNew
				dataSource={dataSource}
				size="small"
				pagination={false}
				onChange={updateEstimatedItems}
				emptyText="No estimated items"
			/>
		</Card>
	);
};

export default EstimatedItemsCard;
